import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import Footer from "./Footer";
import LoginModal from "../Auth/LoginModal";
import ContactForm from "./ContactForm";
import RegisterInterestForm from "./RegisterInterestForm";
import LocationSeo from "./locationSeo";
import { BRAND } from "../../config/locations";
import "../../styles/modern-landing.css";

const DAYS = ["Mon", "Tues", "Wed", "Thurs", "Fri", "Sat"];

// ---------------------------------------------------------------------------
// LocationPage — reusable, config-driven renderer for a single location.
// Renders ONLY the sections a location has data for, sets per-page SEO via
// Helmet, emits SportsActivityLocation JSON-LD, and branches the CTA area on
// the location's status / enquiryType:
//   open  + closer            -> Closer iframe enquiry form
//   coming-soon + register-interest -> RegisterInterestForm
//   open  + booking           -> booking embed (placeholder until wired)
//
// Rich locations (e.g. Sidcup) use their own dedicated page; this generic page
// is what Ebbsfleet uses and what future locations can adopt by adding data.
// ---------------------------------------------------------------------------
const LocationPage = ({ location }) => {
	const [showLogin, setShowLogin] = useState(false);
	const { user, logout } = useAuth();

	const handleLogout = async () => {
		try {
			await logout();
		} catch (error) {
			console.error("Logout error:", error);
		}
	};

	if (!location) return null;

	const {
		name,
		status,
		enquiryType,
		hero = {},
		tagline,
		classTypes,
		pricing,
		timetable,
		facilities,
		address,
		mapUrl,
		contactEmail,
		phoneDisplay,
	} = location;

	const isComingSoon = status === "coming-soon";
	const hasStaticTimetable =
		timetable?.source === "static" && timetable?.schedule;
	const hasClassTypes = Array.isArray(classTypes) && classTypes.length > 0;
	const hasFacilities = Array.isArray(facilities) && facilities.length > 0;
	const hasPricing = pricing && pricing.note;
	const hasAddress =
		address?.streetAddress && !String(address.streetAddress).startsWith("TODO");

	const scrollToCta = () => {
		const id = isComingSoon ? "register-interest" : "contact";
		const el = document.getElementById(id);
		if (el) el.scrollIntoView({ behavior: "smooth" });
	};

	const renderCta = () => {
		if (isComingSoon || enquiryType === "register-interest") {
			return <RegisterInterestForm location={location} />;
		}
		if (enquiryType === "closer") {
			return <ContactForm formId={location.closerFormId} />;
		}
		if (enquiryType === "booking") {
			// TODO: wire booking embed when a location switches to 'booking'.
			return <ContactForm formId={location.closerFormId} />;
		}
		return null;
	};

	return (
		<div className="min-vh-100">
			<LocationSeo location={location} />

			{/* Navbar */}
			<nav
				className="modern-navbar"
				role="navigation"
				aria-label="Main navigation"
			>
				<div className="modern-navbar-container">
					<Link className="modern-navbar-brand" to="/">
						<img
							src={process.env.PUBLIC_URL + "/EU59_LogoAsset 7@4x.png"}
							alt="Urban 59"
							style={{ height: "60px", width: "auto" }}
						/>
						{location?.shortName && (
							<span className="modern-navbar-location">
								{location.shortName}
							</span>
						)}
					</Link>

					<div className="modern-navbar-menu">
						<Link to="/" className="modern-nav-link">
							All Locations
						</Link>
						<a href="#about" className="modern-nav-link">
							About
						</a>
						{!isComingSoon && (
							<a href="#contact" className="modern-nav-link">
								Contact
							</a>
						)}
					</div>

					<div className="modern-navbar-actions">
						{user ? (
							<>
								<span className="modern-user-text">Welcome, {user.email}</span>
								<button className="modern-btn-logout" onClick={handleLogout}>
									Logout
								</button>
							</>
						) : (
							<button
								className="modern-btn-trial"
								onClick={() => setShowLogin(true)}
							>
								LOGIN
							</button>
						)}
					</div>
				</div>

				{/* Location banner (mobile only) — inside the fixed navbar. */}
				{location?.shortName && (
					<div className="modern-location-banner">{location.shortName}</div>
				)}
			</nav>

			{/* Hero */}
			<section className="modern-hero-section" aria-label="Hero section">
				<div className="modern-hero-container">
					<div className="modern-hero-content">
						{hero.backgroundVideo && (
							<video
								autoPlay
								loop
								muted
								playsInline
								poster={
									hero.poster
										? process.env.PUBLIC_URL + "/" + hero.poster
										: undefined
								}
								style={{
									position: "absolute",
									top: 0,
									left: 0,
									width: "100%",
									height: "100%",
									objectFit: "cover",
									zIndex: 0,
									opacity: 0.3,
									borderRadius: "inherit",
								}}
							>
								<source
									src={process.env.PUBLIC_URL + "/" + hero.backgroundVideo}
									type="video/mp4"
								/>
							</video>
						)}
						<div className="modern-hero-text">
							{isComingSoon && (
								<span
									className="section-label"
									style={{ display: "inline-block", marginBottom: "0.75rem" }}
								>
									Coming Soon
								</span>
							)}
							<h1 className="modern-hero-title" itemProp="headline">
								{(hero.title || name).split("\n").map((line, i) => (
									<React.Fragment key={i}>
										{line}
										<br />
									</React.Fragment>
								))}
							</h1>
							{hero.description && (
								<p className="modern-hero-description">{hero.description}</p>
							)}

							<div className="modern-hero-actions">
								<button className="modern-btn-primary" onClick={scrollToCta}>
									{isComingSoon ? "Register Interest" : "Enquire Now"}
									<svg
										width="20"
										height="20"
										viewBox="0 0 20 20"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M7.5 15L12.5 10L7.5 5"
											stroke="currentColor"
											strokeWidth="2"
											strokeLinecap="round"
											strokeLinejoin="round"
										/>
									</svg>
								</button>
							</div>
						</div>

						{hero.image && (
							<div className="modern-hero-image">
								<img
									src={process.env.PUBLIC_URL + "/" + hero.image}
									alt={name}
									className="modern-hero-img"
								/>
							</div>
						)}
					</div>
				</div>
			</section>

			{/* About / tagline */}
			<section
				id="about"
				className="modern-about-section"
				aria-labelledby="about-heading"
			>
				<div className="container">
					<div className="modern-about-header">
						<p className="section-label">{name}</p>
						<h2 id="about-heading" className="section-title">
							<span className="highlight">{name}</span>
						</h2>
						{tagline && <p className="modern-about-description">{tagline}</p>}
					</div>
				</div>
			</section>

			{/* Class types (only if data) */}
			{hasClassTypes && (
				<section
					id="classes"
					className="features-section"
					aria-labelledby="classes-heading"
				>
					<div className="features-section-wrapper">
						<div className="container">
							<div className="row text-center mb-5">
								<div className="col-lg-8 mx-auto">
									<p className="section-label">What we offer</p>
									<h2 id="classes-heading" className="section-title">
										Class <span className="highlight">Types</span>
									</h2>
								</div>
							</div>
							<div className="row g-4">
								{classTypes.map((ct, i) => (
									<div key={i} className="col-md-4">
										<div style={{ textAlign: "center", padding: "1rem" }}>
											<h4
												style={{
													color: "#ffffff",
													fontWeight: 800,
													textTransform: "uppercase",
												}}
											>
												{ct.title}
											</h4>
											{ct.description && (
												<p style={{ color: "#ffffff" }}>{ct.description}</p>
											)}
										</div>
									</div>
								))}
							</div>
						</div>
					</div>
				</section>
			)}

			{/* Pricing (only if data) */}
			{hasPricing && (
				<section id="pricing" className="modern-about-section">
					<div className="container">
						<div className="modern-about-header">
							<p className="section-label">Pricing</p>
							<h2 className="section-title">
								Membership <span className="highlight">Options</span>
							</h2>
							<p className="modern-about-description">{pricing.note}</p>
						</div>
					</div>
				</section>
			)}

			{/* Timetable (only if static data) */}
			{hasStaticTimetable && (
				<section
					id="timetable"
					className="timetable-section"
					aria-labelledby="timetable-heading"
				>
					<div className="container">
						<div className="timetable-header">
							<p className="section-label">Schedule</p>
							<h2 id="timetable-heading" className="section-title">
								Session <span className="highlight">Schedule</span>
							</h2>
						</div>
						<div className="timetable-container">
							<table className="timetable">
								<thead>
									<tr>
										{DAYS.map((day) => (
											<th key={day}>{day}</th>
										))}
									</tr>
								</thead>
								<tbody>
									{(() => {
										const sched = timetable.schedule;
										const allTimes = [
											...new Set(
												DAYS.flatMap((day) =>
													(sched[day] || []).map((slot) => slot.time),
												),
											),
										].sort();
										const hours = [
											...new Set(allTimes.map((t) => t.split(":")[0])),
										].sort();
										return hours.map((hour) => (
											<tr key={hour}>
												{DAYS.map((day) => {
													const slot = (sched[day] || []).find(
														(s) => s.time.split(":")[0] === hour,
													);
													if (!slot) return <td key={day}></td>;
													return (
														<td key={day} className="time-slot">
															{slot.time}
															{slot.note && "*"}
														</td>
													);
												})}
											</tr>
										));
									})()}
								</tbody>
							</table>
						</div>
					</div>
				</section>
			)}

			{/* Facilities (only if data) */}
			{hasFacilities && (
				<section id="facilities" className="modern-about-section">
					<div className="container">
						<div className="modern-about-header">
							<p className="section-label">The space</p>
							<h2 className="section-title">
								Facilities <span className="highlight">&amp; Features</span>
							</h2>
						</div>
						<div className="modern-stats-grid">
							{facilities.map((f, i) => (
								<div key={i} className="modern-stat-item">
									<div className="modern-stat-label">{f}</div>
								</div>
							))}
						</div>
					</div>
				</section>
			)}

			{/* Location / address (only if a real address is set) */}
			{hasAddress && (
				<section id="find-us" className="modern-about-section">
					<div className="container">
						<div className="modern-about-header">
							<p className="section-label">Find us</p>
							<h2 className="section-title">
								Where to <span className="highlight">Find Us</span>
							</h2>
							<p className="modern-about-description">
								{address.streetAddress}, {address.addressLocality},{" "}
								{address.addressRegion} {address.postalCode}
							</p>
							{mapUrl && (
								<p>
									<a
										href={mapUrl}
										target="_blank"
										rel="noopener noreferrer"
										className="modern-nav-link"
									>
										Open in Google Maps
									</a>
								</p>
							)}
							<p>
								{contactEmail && (
									<>
										Email: <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
									</>
								)}
								{phoneDisplay && (
									<>
										{"  •  "}Phone:{" "}
										<a href={`tel:${location.phone || BRAND.phone}`}>
											{phoneDisplay}
										</a>
									</>
								)}
							</p>
						</div>
					</div>
				</section>
			)}

			{/* CTA: enquiry or register-interest */}
			{renderCta()}

			<Footer location={location} />

			<LoginModal show={showLogin} onClose={() => setShowLogin(false)} />
		</div>
	);
};

export default LocationPage;
